import {t} from 'i18next';

import {sanitizeCommentContent} from './commentContent';

export const hasMeaningfulCommentDraft = (content: string | undefined | null): boolean =>
  sanitizeCommentContent(content).trim().length > 0;

export const confirmDiscardCommentDraft = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  return window.confirm(t('get_rid_of_comment_you_wrote'));
};

export const preventNextClickAfterDiscardCancel = (event?: MouseEvent): void => {
  event?.preventDefault();
  event?.stopPropagation();
  event?.stopImmediatePropagation?.();

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const blockClick = (clickEvent: MouseEvent) => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();
    clickEvent.stopImmediatePropagation?.();
    document.removeEventListener('click', blockClick, true);
  };

  document.addEventListener('click', blockClick, true);
  window.setTimeout(() => document.removeEventListener('click', blockClick, true), 500);
};

const COMMENT_CLOSE_CLICK_THROUGH_TARGET_SELECTOR = [
  'button',
  '[role="button"]',
  'a[href]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  '[role="menuitem"]',
  '[role="option"]',
  'summary',
].join(',');

// CommentDisplay closes from a capture-phase mousedown so outside clicks are detected
// before editor handlers can swallow them. For real controls, we still need to let
// the browser deliver the matching click event to the target before unmounting.
export const shouldLetOutsideCommentClickThrough = (event?: MouseEvent): boolean => {
  if (!event || event.type !== 'mousedown' || event.button !== 0) {
    return false;
  }

  if (!(event.target instanceof Element)) {
    return false;
  }

  return Boolean(event.target.closest(COMMENT_CLOSE_CLICK_THROUGH_TARGET_SELECTOR));
};

// Defers closing until after the click event that follows the outside mousedown.
// This preserves the original button/menu action while keeping a fallback for
// cases where the browser never emits a click, such as drag or canceled pointer flows.
export const deferCommentCloseUntilTargetClick = (
  callback: () => void,
  fallbackMs = 750
): (() => void) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    callback();
    return () => undefined;
  }

  let fallbackTimer: number | undefined;
  let closeTimer: number | undefined;
  let isDone = false;

  function cleanupListenerAndFallback() {
    document.removeEventListener('click', handleClick, true);
    if (fallbackTimer !== undefined) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = undefined;
    }
  }

  function finish(deferToNextTask: boolean) {
    if (isDone) return;

    isDone = true;
    cleanupListenerAndFallback();

    if (deferToNextTask) {
      closeTimer = window.setTimeout(() => {
        closeTimer = undefined;
        callback();
      }, 0);
      return;
    }

    callback();
  }

  function handleClick() {
    finish(true);
  }

  document.addEventListener('click', handleClick, true);
  fallbackTimer = window.setTimeout(() => finish(false), fallbackMs);

  return () => {
    isDone = true;
    cleanupListenerAndFallback();
    if (closeTimer !== undefined) {
      window.clearTimeout(closeTimer);
      closeTimer = undefined;
    }
  };
};
